import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_BASE_URL } from '@fdm/api-client';
import { Observable, tap } from 'rxjs';
import { RiskEvaluationCreatePayload, RiskEvaluationFormData, RiskEvaluationUpdatePayload } from '../models';

@Injectable({
  providedIn: 'root'
})
export class RiskEvaluationApiService {

  private _http = inject(HttpClient);

  private _baseUrl = inject(API_BASE_URL);

  private _url = this._baseUrl + "/api/RiskEvaluation";

  public getRiskEvaluationById(generalTabId: string): Observable<RiskEvaluationFormData> {
    return this._http.get<RiskEvaluationFormData>(`${this._url}/by-general-tab/${generalTabId}`);
  }

  public create(payload: RiskEvaluationCreatePayload) {
    const content = new FormData();

    if (payload.healthAndSafetyComment === null || payload.healthAndSafetyComment === undefined)
      throw new globalThis.Error("The parameter 'healthAndSafetyComment' cannot be null.");
    else
      content.append("RiskCommentDto.HealthAndSafetyComment", payload.healthAndSafetyComment);

    if (payload.environmentalComment === null || payload.environmentalComment === undefined)
      throw new globalThis.Error("The parameter 'environmentalComment' cannot be null.");
    else
      content.append("RiskCommentDto.EnvironmentalComment", payload.environmentalComment.toString());

    if (payload.reputationComment === null || payload.reputationComment === undefined)
      throw new globalThis.Error("The parameter 'reputationComment' cannot be null.");
    else
      content.append("RiskCommentDto.ReputationComment", payload.reputationComment.toString());

    if (payload.assetComment === null || payload.assetComment === undefined)
      throw new globalThis.Error("The parameter 'payload.assetComment' cannot be null.");
    else
      content.append("RiskCommentDto.AssetComment", payload.assetComment.toString());
    
    if (payload.riskAssessmentComments === null || payload.riskAssessmentComments === undefined)
      throw new globalThis.Error("The parameter 'payload.riskAssessmentComments' cannot be null.");
    else
      content.append("RiskCommentDto.RiskAssessmentComments", payload.riskAssessmentComments.toString());
    
    if (payload.attachRiskEvaluationForm?.data) {
      content.append("RiskCommentDto.AttachRiskEvaluationForm", payload.attachRiskEvaluationForm.data, payload.attachRiskEvaluationForm.fileName ? payload.attachRiskEvaluationForm.fileName : "RiskCommentDto.AttachRiskEvaluationForm");
    }
    
    if (payload.scores === null || payload.scores === undefined)
      throw new globalThis.Error("The parameter 'RiskEvaluationDtos' cannot be null.");
    else {
      payload.scores.forEach((item, idx) => {
        content.append(`RiskEvaluationDtos[${idx}].GeneralTabId`, item.generalTabId?.toString() ?? '');
        content.append(`RiskEvaluationDtos[${idx}].Category`, item.category?.toString() ?? '');
        content.append(`RiskEvaluationDtos[${idx}].Consequence`, item.consequence?.toString() ?? '');
        content.append(`RiskEvaluationDtos[${idx}].Likelihood`, item.likelihood?.toString() ?? '');
      });
    }

    return this._http.post(this._url, content);
  }

  public update(payload: RiskEvaluationUpdatePayload) {
    const content = new FormData();

    if (payload.id === null || payload.id === undefined)
      throw new globalThis.Error("The parameter 'id' cannot be null.");
    else
      content.append("RiskCommentDto.Id", payload.id.toString());
    
    if (payload.healthAndSafetyComment === null || payload.healthAndSafetyComment === undefined)
      throw new globalThis.Error("The parameter 'healthAndSafetyComment' cannot be null.");
    else
      content.append("RiskCommentDto.HealthAndSafetyComment", payload.healthAndSafetyComment);

    if (payload.environmentalComment === null || payload.environmentalComment === undefined)
      throw new globalThis.Error("The parameter 'environmentalComment' cannot be null.");
    else
      content.append("RiskCommentDto.EnvironmentalComment", payload.environmentalComment.toString());

    if (payload.reputationComment === null || payload.reputationComment === undefined)
      throw new globalThis.Error("The parameter 'reputationComment' cannot be null.");
    else
      content.append("RiskCommentDto.ReputationComment", payload.reputationComment.toString());

    if (payload.assetComment === null || payload.assetComment === undefined)
      throw new globalThis.Error("The parameter 'assetComment' cannot be null.");
    else
      content.append("RiskCommentDto.AssetComment", payload.assetComment.toString());
    
    if (payload.riskAssessmentComments === null || payload.riskAssessmentComments === undefined)
      throw new globalThis.Error("The parameter 'riskAssessmentComments' cannot be null.");
    else
      content.append("RiskCommentDto.RiskAssessmentComments", payload.riskAssessmentComments.toString());
    
    if (payload.attachRiskEvaluationForm?.data) 
      content.append("RiskCommentDto.AttachRiskEvaluationForm", payload.attachRiskEvaluationForm.data, payload.attachRiskEvaluationForm.fileName ? payload.attachRiskEvaluationForm.fileName : "RiskCommentDto.AttachRiskEvaluationForm");
    
    if (payload.attachRiskEvaluationFormPath)
      content.append("RiskCommentDto.AttachRiskEvaluationFormPath", payload.attachRiskEvaluationFormPath);
    
    if (payload.scores === null || payload.scores === undefined)
      throw new globalThis.Error("The parameter 'RiskEvaluationDtos' cannot be null.");
    else {
      payload.scores.forEach((item, idx) => {
        content.append(`RiskEvaluationDtos[${idx}].GeneralTabId`, item.generalTabId?.toString() ?? '');
        content.append(`RiskEvaluationDtos[${idx}].Category`, item.category?.toString() ?? '');
        content.append(`RiskEvaluationDtos[${idx}].Consequence`, item.consequence?.toString() ?? '');
        content.append(`RiskEvaluationDtos[${idx}].Likelihood`, item.likelihood?.toString() ?? '');
      });
    }

    return this._http.put(this._url, content);
  }

  public download(blobName: string) {
    console.log('blobname', blobName);
    
    return this._http.get(`${this._url}/download/${encodeURIComponent(blobName)}`, {
      responseType: 'blob'
    }).pipe(tap(blob => this._processDownloading(blobName, blob)));
  }

  private _processDownloading(fileName: string, blob: Blob) {
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(objectUrl);
  }
}
